import { ApplicationConfig, provideZonelessChangeDetection } from '@angular/core';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { provideNativeDateAdapter } from '@angular/material/core';
import { provideRouter, withComponentInputBinding } from '@angular/router';
import { OVERLAY_DEFAULT_CONFIG } from '@angular/cdk/overlay';
import {
  centralHttpErrorInterceptor,
  definitionRevisionInterceptor,
  DESIGN_ROUTE_PREFIX,
  LayoutService,
  provideCentralErrorHandler,
  provideErrorSnackbar,
  provideLoggingService,
  provideTranslocoService,
  RUNTIME_CONFIGURATION,
  TRANSLATION_SOURCE_REGISTRY,
} from '@processpuzzle/util';
import { AUTHENTICATION_CONFIGURATION, authTokenInterceptorFor, provideAuthenticationService, provideRoleCatalogSuggestions, ROLE_DESCRIPTION_SOURCE } from '@processpuzzle/auth/domain';
import {
  BASE_ENTITY_AUTHORING_ENTITY_FACADES,
  BASE_ENTITY_AUTHORING_FACADE_PROVIDERS,
  BASE_ENTITY_FACADE_REGISTRY,
  BASE_ENTITY_TRANSLATION_SOURCE,
  provideEntityRouteRegistry,
} from '@processpuzzle/base-entity';
import {
  BASE_WIDGET_ENTITY_FACADES,
  BASE_WIDGET_FACADE_PROVIDERS,
  BASE_WIDGET_TRANSLATION_SOURCE,
  provideBaseWidgets,
} from '@processpuzzle/base-widget';
import { BASE_APP_ENTITY_FACADES, BASE_APP_FACADE_PROVIDERS, BASE_APP_TRANSLATION_SOURCE } from '@processpuzzle/base-app';
import { BASE_DOCUMENT_ENTITY_FACADES, BASE_DOCUMENT_FACADE_PROVIDERS, BASE_DOCUMENT_TRANSLATION_SOURCE } from '@processpuzzle/base-document';
import { provideBaseRuleEngine } from '@processpuzzle/base-rule';
import { BASE_STATE_ENTITY_FACADES, BASE_STATE_FACADE_PROVIDERS, BASE_STATE_TRANSLATION_SOURCE, provideEntityStateControl, provideEntityStateMachineTab } from '@processpuzzle/base-state';
import { BASE_WORKFLOW_ENTITY_FACADES, BASE_WORKFLOW_FACADE_PROVIDERS, BASE_WORKFLOW_TRANSLATION_SOURCE, WorkflowRoleDescriptionSource } from '@processpuzzle/base-workflow';
import { BASE_CALCULATION_ENTITY_FACADES, BASE_CALCULATION_FACADE_PROVIDERS, BASE_CALCULATION_TRANSLATION_SOURCE, provideEntityCalculatedControl } from '@processpuzzle/base-calculation';
import { BASE_EVENT_ENTITY_FACADES, BASE_EVENT_FACADE_PROVIDERS, BASE_EVENT_TRANSLATION_SOURCE } from '@processpuzzle/base-event';
import { ORG_ADMIN_ENTITY_FACADES, ORG_ADMIN_FACADE_PROVIDERS, ORG_ADMIN_ORG_KEY, ORG_ADMIN_TRANSLATION_SOURCE } from '@processpuzzle/org-admin';
import { RuntimeConfiguration } from './runtime-configuration';
import { createAppRoutes } from './app.routes';
import { provideCustomApplicationContext } from './navigation/application-context';
import { UNKNOWN_ORGANIZATION } from './no-organization/unknown-organization';
import { providePageRevision } from './runtime/page-revision';
import { provideCustomApplicationProperties } from './widget/custom-application-property.providers';

/**
 * @param runtimeConfiguration the deep-merged `run-time-conf` files. Unlike Biz's, its realm is
 *   **not** rewritten by `main.ts`: every customer authenticates against the one
 *   `processpuzzle-custom` realm and is distinguished by Keycloak Organization. See `main.ts`.
 * @param orgKey the organization the URL named, or `undefined` when it named none
 * @param unknownOrgKey the key the URL named when Keycloak knows no organization by it; the
 *   application then starts without one, and the no-organization page names it
 */
export function createAppConfig(runtimeConfiguration: RuntimeConfiguration, orgKey?: string, unknownOrgKey?: string): ApplicationConfig {
  const orgAdminRoot = runtimeConfiguration.BASE_CONFIGURATION.ORG_ADMIN_SERVICE_ROOT;

  return {
    providers: [
      provideZonelessChangeDetection(),
      // One `provideHttpClient` with both interceptors, rather than `provideAuthTokenInterceptor()`
      // next to a second `provideHttpClient` — two calls compete and only one chain survives.
      //
      // The auth interceptor is given the org-admin root and nothing else. Every entry is a host
      // this application is willing to hand a customer's token to, and that token carries the
      // `organization` claim the custom backend authorizes against.
      //
      // `definitionRevisionInterceptor` addresses every definition read to this page's revision — see below.
      provideHttpClient(
        withInterceptors([definitionRevisionInterceptor, authTokenInterceptorFor(orgAdminRoot ? [orgAdminRoot] : []), centralHttpErrorInterceptor]),
      ),
      // Drafts inside the designer, read-only on live objects; released definitions everywhere else. Settled per
      // page: crossing between the two loads the page anew (`pageRevisionGuard` in the routes).
      ...providePageRevision(orgKey),
      provideAuthenticationService(runtimeConfiguration),
      provideLoggingService(runtimeConfiguration.LOGGING_CONFIGURATION),
      provideCentralErrorHandler(),
      provideErrorSnackbar(),

      // ── The designer's mount point ──────────────────────────────────────────────────────────
      //
      // The design surfaces address themselves absolutely — `DesignRouteService` tests
      // `startsWith('/design')`, the design button links to `/design` and back to `/home`, and the
      // design sidenav and Designer Home cards build `/design/...`. All of that is right for a
      // shell whose designer is at the root, and wrong for this one, whose organization is the
      // first path segment. This token is what reconciles them; it defaults to `''`, so no other
      // application is affected. Without it every one of those four fails silently and
      // differently — see DESIGN_ROUTE_PREFIX.
      { provide: DESIGN_ROUTE_PREFIX, useValue: orgKey ? `/${orgKey}` : '' },

      // Application level, not route level, and for two separate reasons. `ORG_ADMIN_ORG_KEY` is
      // injected while `OrganizationUserService` — `providedIn: 'root'` — is constructed, which
      // happens before any route activates. And the library routes bind `ACTIVE_ENTITY_FACADE`
      // with `useExisting`, which needs the facade instance to already exist; a route-level
      // provider would create a second one per activation and lose whatever the first had loaded.
      { provide: ORG_ADMIN_ORG_KEY, useValue: orgKey ?? '' },
      { provide: UNKNOWN_ORGANIZATION, useValue: unknownOrgKey },
      ...ORG_ADMIN_FACADE_PROVIDERS,

      // ── The designer's facades ──────────────────────────────────────────────────────────────
      //
      // Every feature mounted under `DESIGN_ROUTES` resolves its store and descriptor through
      // `BASE_ENTITY_FACADE_REGISTRY`, and no library can contribute to that token without
      // replacing it — so the hosting application spreads all of them, as `DESIGN_ROUTES`'
      // own comments require. Each list is whole-graph or nothing: a workflow task's
      // `performedBy` resolves through the role facade and a step's `toolId` through the tool
      // facade, so half a graph is a form that throws on render.
      ...BASE_ENTITY_AUTHORING_FACADE_PROVIDERS,
      ...BASE_APP_FACADE_PROVIDERS,
      ...BASE_DOCUMENT_FACADE_PROVIDERS,
      ...BASE_WIDGET_FACADE_PROVIDERS,
      ...BASE_STATE_FACADE_PROVIDERS,
      ...BASE_WORKFLOW_FACADE_PROVIDERS,
      // The event catalog: the Workflows section's Events tab, and the Event pickers of a workflow's
      // start events and throw/catch events — base-workflow names an event by id without importing
      // base-event, so this registration is also what fills those pickers.
      ...BASE_EVENT_FACADE_PROVIDERS,
      // The Calculations section of the designer, and the CALCULATED form control documents and reports read through.
      ...BASE_CALCULATION_FACADE_PROVIDERS,

      // Adds the read-only State Machine tab to every entity a state machine governs. The customer
      // declares both the entity and the machine here, so this is what makes the second show up on
      // the first without the customer wiring anything.
      ...provideEntityStateMachineTab(),
      // And turns the attribute that machine governs into a STATE control on the entity's form: the
      // current state plus the transitions allowed from it, instead of a free edit of the value.
      ...provideEntityStateControl(),
      // Likewise the attributes a released calculation writes: rendered read-only, with the calculation behind them.
      ...provideEntityCalculatedControl(),

      // Fills WIDGET_REGISTRY with the components behind the catalogue's keys. base-app's shell
      // renders a widget instance by looking its `type` up there and provides nothing itself by
      // design — which component answers a key is the hosting application's decision. Without this
      // the Application designer's preview renders empty boxes.
      provideBaseWidgets(),

      // The header's like button reads and writes application properties, and core's store for them needs a service
      // only the testbed has: without this it throws NG0201 in every change-detection pass. See the function.
      ...provideCustomApplicationProperties(),

      // Binds base-entity's `RULE_ENGINE` seam to base-rule's evaluator, application-wide.
      // `BaseEntityFormComponent` injects it optionally: unbound, `loadRules()` returns on its
      // first line and every form is silently unvalidated. Application level and not on the design
      // branch, because rules are a property of the entity being edited rather than of the section
      // the customer happens to be in — the customer authors a rule in the designer and expects it
      // to hold on the running screens too.
      provideBaseRuleEngine(),

      {
        provide: BASE_ENTITY_FACADE_REGISTRY,
        useValue: {
          ...ORG_ADMIN_ENTITY_FACADES,
          ...BASE_ENTITY_AUTHORING_ENTITY_FACADES,
          ...BASE_APP_ENTITY_FACADES,
          ...BASE_DOCUMENT_ENTITY_FACADES,
          ...BASE_WIDGET_ENTITY_FACADES,
          ...BASE_STATE_ENTITY_FACADES,
          ...BASE_WORKFLOW_ENTITY_FACADES,
          ...BASE_EVENT_ENTITY_FACADES,
          ...BASE_CALCULATION_ENTITY_FACADES,
          // Nothing of the customer's own is registered here, and that is the point of the
          // product: the entities a customer declares in the designer are metadata, and
          // base-entity's `EntityScreenResolver` synthesizes their descriptors at run time. A
          // compile-time facade wins by design, so an entry here would *override* the customer's
          // own definition — which is the one thing this shell must never do.
        },
      },

      // Which backend serves which transloco scope, for bundles with no asset to fall back on. All
      // contributions have to be here rather than on a route branch: a `multi` token is not merged
      // across injectors, so a route-level entry replaces the collection instead of extending it.
      //
      // No `platform_admin` source, matching the routes: those screens are staff-only and this
      // shell does not carry them.
      ...[
        ORG_ADMIN_TRANSLATION_SOURCE,
        BASE_ENTITY_TRANSLATION_SOURCE,
        BASE_WIDGET_TRANSLATION_SOURCE,
        BASE_APP_TRANSLATION_SOURCE,
        BASE_DOCUMENT_TRANSLATION_SOURCE,
        BASE_STATE_TRANSLATION_SOURCE,
        BASE_WORKFLOW_TRANSLATION_SOURCE,
        BASE_EVENT_TRANSLATION_SOURCE,
        BASE_CALCULATION_TRANSLATION_SOURCE,
      ].map((source) => ({
        provide: TRANSLATION_SOURCE_REGISTRY,
        useValue: source,
        multi: true,
      })),
      { provide: OVERLAY_DEFAULT_CONFIG, useValue: { usePopover: false } },
      { provide: RUNTIME_CONFIGURATION, useValue: runtimeConfiguration },
      { provide: AUTHENTICATION_CONFIGURATION, useValue: runtimeConfiguration.AUTHENTICATION_CONFIGURATION },
      // My Profile describes the user's roles from the organization's role definitions, the same catalog the designer
      // edits. Without it a role the customer defined is listed by its bare name.
      { provide: ROLE_DESCRIPTION_SOURCE, useExisting: WorkflowRoleDescriptionSource },
      // Role fields on documents and content books offer the organization's role catalog, as the Testbed's do.
      provideRoleCatalogSuggestions(),
      provideRouter(createAppRoutes(orgKey), withComponentInputBinding()),
      provideEntityRouteRegistry(),
      provideNativeDateAdapter(),
      provideTranslocoService(runtimeConfiguration.LANGUAGE_CONFIGURATION),
      LayoutService,
      // The name and navigation the workspace header's `app-title` and `nav-menu` widgets read. The
      // workspace chrome is hand-built rather than a base-app `AppShellComponent`, so it provides the
      // context itself; the application, in `RuntimeHostComponent`, is an `AppShellComponent` with its own.
      provideCustomApplicationContext(),
    ],
  };
}
